import { useRef, useState } from "react";
import { Camera, Loader2, RotateCcw, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { enqueue, isNetworkError } from "../../lib/offlineQueue";
import { ContactFields, EMPTY_CONTACT } from "./ContactFields";

// Downscale phone photos (often 4–8 MB) to a ~1600px JPEG before upload.
const shrinkImage = (file, maxEdge = 1600) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => resolve(b || file), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

const ScanPrompt = ({ onPick, busy }) => (
  <label
    data-testid="card-scan-capture"
    className="flex flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-brand-teal/40 bg-white py-14 cursor-pointer active:scale-[0.99] transition-transform"
  >
    {busy ? <Loader2 className="h-10 w-10 animate-spin text-brand-teal" /> : <Camera className="h-10 w-10 text-brand-teal" />}
    <span className="font-display font-black text-xl text-brand-charcoal">{busy ? "Reading card…" : "Take a photo of the card"}</span>
    <span className="text-[13px] text-brand-charcoal/60 px-6 text-center">
      {busy ? "This takes a few seconds." : "Lay it flat with good light. You'll check the details before saving."}
    </span>
    <input
      type="file"
      accept="image/*"
      capture="environment"
      className="hidden"
      disabled={busy}
      data-testid="card-scan-input"
      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }}
    />
  </label>
);

export const CardScanSheet = ({ eventId, onSaved, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cardId, setCardId] = useState(null);
  const [form, setForm] = useState(EMPTY_CONTACT);
  const scanned = useRef(false);

  const reset = (discard = false) => {
    if (discard && cardId) api.delete(`/admin/event-cards/${cardId}`).catch(() => {});
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null); setCardId(null); setForm(EMPTY_CONTACT); scanned.current = false;
  };
  const close = () => { reset(true); onClose(); };

  const onPick = async (file) => {
    setBusy(true);
    let blob = file;
    try {
      blob = await shrinkImage(file);
      if (!navigator.onLine) throw Object.assign(new Error("offline"), { offline: true });
      setPreview(URL.createObjectURL(blob));
      const fd = new FormData();
      fd.append("file", blob, "card.jpg");
      const { data } = await api.post("/admin/events/scan-card", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 25000 });
      setCardId(data.card_image_id);
      setForm({ ...EMPTY_CONTACT, ...data.fields });
      scanned.current = true;
      if (!data.read_ok) toast.warning("Couldn't read this card clearly — fill in the details or retake.");
    } catch (err) {
      if (err.offline || isNetworkError(err)) {
        await enqueue({ type: "card", event_id: eventId, blob });
        toast.success("Card saved on this phone — it'll be read when you're back online.");
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed. Try again.");
      }
      reset();
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/events/${eventId}/contacts`, {
        ...form, email: form.email.trim() || null, source: "card", card_image_id: cardId,
      });
      toast.success(`Saved ${data.contact.name || "contact"}`);
      onSaved(data.contact);
      reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save contact.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="card-scan-sheet" className="fixed inset-0 z-50 bg-brand-cream overflow-y-auto">
      <div className="max-w-lg mx-auto px-4 pt-4 pb-28">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-black text-2xl text-brand-charcoal">Scan a card</p>
          <button type="button" onClick={close} data-testid="card-scan-close" className="p-2 rounded-full bg-white border border-black/10" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {!scanned.current ? (
          <>
            {preview && <img src={preview} alt="Card preview" className="mb-3 w-full rounded-2xl opacity-60" />}
            <ScanPrompt onPick={onPick} busy={busy} />
          </>
        ) : (
          <div className="space-y-4">
            <img src={preview} alt="Scanned card" data-testid="card-scan-preview" className="w-full rounded-2xl border border-black/10" />
            <ContactFields value={form} onChange={setForm} testidPrefix="card-scan" />
          </div>
        )}
      </div>
      {scanned.current && (
        <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-black/10 p-3">
          <div className="max-w-lg mx-auto grid grid-cols-2 gap-2">
            <button type="button" onClick={() => reset(true)} data-testid="card-scan-retake" className="inline-flex items-center justify-center gap-2 h-12 rounded-full border border-black/15 font-bold text-[13px] uppercase tracking-[0.1em]">
              <RotateCcw className="h-4 w-4" /> Retake
            </button>
            <button type="button" onClick={save} disabled={saving} data-testid="card-scan-save" className="inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-charcoal text-white font-bold text-[13px] uppercase tracking-[0.1em] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save & next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardScanSheet;
